import { useQuery } from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError, HTTPErrors } from "../../errors";
import { paths } from "../../lib/api/v1";

const URL =
  "/api/v1/sections/{section_id}/timeline/drill_progress/study_records/{study_record_hashid}";
type Url = typeof URL;

type StudyRecordContentsWithAnswersResponse =
  paths[Url]["get"]["responses"]["200"]["content"]["application/json"];

export type DrillContentsWithAnswers = NonNullable<
  StudyRecordContentsWithAnswersResponse["drillContentsWithAnswers"]
>;

export type DrillContentItem = NonNullable<DrillContentsWithAnswers[number]>;

export type DrillSection = NonNullable<
  StudyRecordContentsWithAnswersResponse["drillSection"]
>;

type Props = {
  sectionId: string;
  studyRecordId: string;
  enabled: boolean;
};

/**
 * デジタル教材の勉強記録に紐づく回答詳細を取得するフック
 *
 * タイムラインのドリル学習記録をクリックした際に、
 * 回答の詳細情報（問題文、選択肢、正解、回答履歴）を取得します。
 */
export const useFetchStudyRecordContentsWithAnswers = ({
  sectionId,
  studyRecordId,
  enabled,
}: Props) => {
  return useQuery<StudyRecordContentsWithAnswersResponse, HTTPErrors>({
    queryKey: [URL, sectionId, studyRecordId],
    queryFn: () =>
      fetchStudyRecordContentsWithAnswers({ sectionId, studyRecordId }),
    enabled,
    // 回答詳細が存在しない場合（404）はリトライしない
    retry: false,
  });
};

const fetchStudyRecordContentsWithAnswers = async ({
  sectionId,
  studyRecordId,
}: {
  sectionId: string;
  studyRecordId: string;
}): Promise<StudyRecordContentsWithAnswersResponse> => {
  const { data, response } = await boronClient.GET(URL, {
    params: {
      path: {
        section_id: sectionId,
        study_record_hashid: studyRecordId,
      },
    },
  });

  if (response.ok && data) {
    return data;
  }

  throw await createError(response);
};
